/**
 * Document-first account-setup store — Zustand (P1).
 *
 * Drives the `/app/setup` journey state machine: questionnaire → upload →
 * processing → gaps → confirm → done. Holds the four pieces of journey state
 * the screens share:
 *
 *   - `manifest`  — the expected scope ({ trusts, companies, investments,
 *                   properties }) the questionnaire captured + the server
 *                   persisted (`POST /v1/setup/manifest`). The load-bearing idea:
 *                   the app knows what SHOULD arrive, so upload becomes a
 *                   checklist it reconciles against (spec §3).
 *   - `uploadedDocIds` — the DocumentDoc ids the folder-dump finalised + kicked
 *                   ingest on. Local memory only (there is no "list my setup
 *                   docs" endpoint) — lets the processing screen know how many
 *                   files are in flight + lets a resume show what's already up.
 *   - `skippedGaps` — gap keys the user chose "Skip for now" on (spec §5). Shown
 *                   as tasks on the done screen; they never block completion.
 *   - `step` + `furthestStep` — the current screen + a high-water mark so the
 *                   layout can drive a linear-but-back-navigable machine, exactly
 *                   like the existing onboarding wizard (`onboarding-store.ts`).
 *
 * Persisted to localStorage under a versioned key so a refresh mid-setup doesn't
 * lose the manifest / progress. Cleared on done (`clear()`) so the next setup
 * starts fresh.
 *
 * NB: the assembly (entities + gaps) is NOT persisted here — it is server-derived
 * and re-fetched live via `GET /v1/setup/assembly` (the assembly read is the
 * source of truth; caching it would risk showing stale "ready" state). The store
 * keeps only the inputs (manifest, what we uploaded) + the user's local choices
 * (skips, step).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ── Types ─────────────────────────────────────────────────────────────────────

/** The six steps of the journey, in order. The layout renders the screen for
 *  `step`; `STEP_ORDER` gives the numeric position for the (never-zero) progress
 *  rail + the back/next guard. */
export type SetupStep =
  | 'questionnaire'
  | 'upload'
  | 'processing'
  | 'gaps'
  | 'confirm'
  | 'done';

export const STEP_ORDER: readonly SetupStep[] = [
  'questionnaire',
  'upload',
  'processing',
  'gaps',
  'confirm',
  'done',
];

/** The expected manifest — the scope of the account being set up. Counts only,
 *  no PII (mirrors the backend `SetupManifestDoc.expected`). */
export interface SetupManifest {
  trusts: number;
  companies: number;
  investments: number;
  properties: number;
}

export const EMPTY_MANIFEST: SetupManifest = {
  trusts: 1,
  companies: 0,
  investments: 0,
  properties: 0,
};

export interface SetupStore {
  /** The current screen. */
  step: SetupStep;
  /** The furthest step legitimately reached — lets the rail show progress + lets
   *  the layout allow back-navigation without letting the user skip ahead. */
  furthestStep: SetupStep;
  /** The expected manifest (questionnaire output / server-persisted). Null until
   *  the questionnaire is submitted. */
  manifest: SetupManifest | null;
  /** Optional per-entity names captured in the questionnaire (e.g. trust names),
   *  used only to personalise copy — never required. Index-aligned, sparse. */
  trustNames: string[];
  /** DocumentDoc ids we finalised + kicked ingest on during the folder dump. */
  uploadedDocIds: string[];
  /** The B2C individual upload step's FOUR mandatory slots, keyed by slot key
   *  (`id` | `proof-of-address` | `trust-deed` | `letter-of-authority`). Persisted
   *  so a re-mount of the upload step (e.g. a gap's "Upload now" → back to upload)
   *  preserves the already-uploaded slots — the hard gate reads its done-state from
   *  here, not from component-local state, so "Continue" stays enabled. Transient
   *  uploading/failed state stays local to the step. */
  mandatoryUploads: Record<string, { documentId: string; fileName: string }>;
  /** The client's display name, if the firm typed one ("set up [client]'s
   *  documents"). Personalises the upload + done copy; optional. */
  clientName: string;
  /** Whose setup this is. `'individual'` → the B2C 5-step onboarding wrapper (the
   *  end-customer setting up THEIR OWN trust — "your documents"); `'firm'` /
   *  undefined → the firm operator setting up a client at /app/setup ("the
   *  client's documents"). Drives copy framing only — never gating. */
  accountKind?: 'individual' | 'firm';
  /** B2C guided-upload only: the per-trust answer to "Does this trust hold a
   *  company?", keyed by trust index. `true`/`false` once answered, `undefined`
   *  until the customer answers. A `true` reveals that trust's company-document
   *  upload inline before the panel can advance; the count of `true` answers is
   *  reconciled against the company docs actually uploaded on the final panel.
   *  Persisted so a re-mount of the upload step keeps the answers. */
  trustHasCompany: Record<number, boolean>;
  /** Gap keys the user chose to skip ("Skip for now") — shown as tasks on done. */
  skippedGaps: string[];

  setStep: (step: SetupStep) => void;
  setManifest: (manifest: SetupManifest) => void;
  setTrustNames: (names: string[]) => void;
  setClientName: (name: string) => void;
  setAccountKind: (kind: 'individual' | 'firm') => void;
  /** Record a trust's "holds a company?" answer (B2C guided upload). */
  setTrustHasCompany: (trustIndex: number, hasCompany: boolean) => void;
  addUploadedDocId: (id: string) => void;
  /** Record (or overwrite) a completed mandatory slot. */
  setMandatoryUpload: (key: string, value: { documentId: string; fileName: string }) => void;
  /** Remove a mandatory slot (e.g. the user cleared it). */
  clearMandatoryUpload: (key: string) => void;
  skipGap: (key: string) => void;
  unskipGap: (key: string) => void;
  /** Clear all setup state (called on done / restart). */
  clear: () => void;
}

const STORAGE_KEY = 'elreign:setup:v1';

// ── Step navigation helpers (pure — unit-tested) ──────────────────────────────

/** The numeric position of a step (0-based) in the journey, for progress + the
 *  back/next guard. */
export function stepIndex(step: SetupStep): number {
  const i = STEP_ORDER.indexOf(step);
  return i === -1 ? 0 : i;
}

/** The step that follows `step` (clamped at `done`). */
export function nextStep(step: SetupStep): SetupStep {
  const i = stepIndex(step);
  return STEP_ORDER[Math.min(i + 1, STEP_ORDER.length - 1)]!;
}

/** The step before `step` (clamped at `questionnaire`). */
export function prevStep(step: SetupStep): SetupStep {
  const i = stepIndex(step);
  return STEP_ORDER[Math.max(i - 1, 0)]!;
}

// ── Owner-FICA gate (B2C) ─────────────────────────────────────────────────────

/** The two owner-FICA slot keys in `mandatoryUploads` (the account-holder's own ID
 *  + proof of address) — the ONLY hard-gate documents in the B2C guided upload
 *  (`IndividualUploadStep`'s `MANDATORY_SLOTS`). Defined HERE (the store owns the
 *  `mandatoryUploads` map) so the resume gate can read FICA-completeness without
 *  importing the B2C upload-step route component — which would otherwise drag that
 *  route's bundle onto the shared firm `/app/setup` path. Kept in lockstep with the
 *  upload step's slot keys. */
export const OWNER_FICA_SLOT_KEYS = ['id', 'proof-of-address'] as const;

/** True when BOTH owner-FICA slots carry a finalised `documentId` in the persisted
 *  `mandatoryUploads` map — i.e. the account-holder's own FICA (ID + proof of
 *  address) is in. The B2C resume gate reads this to decide whether to surface the
 *  assembled entities to confirm, or to land the returning user on the owner-FICA
 *  upload panel first. Pure — unit-tested. */
export function ownerFicaComplete(
  mandatoryUploads: Record<string, { documentId: string; fileName: string }>,
): boolean {
  return OWNER_FICA_SLOT_KEYS.every((k) => !!mandatoryUploads[k]?.documentId);
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useSetupStore = create<SetupStore>()(
  persist(
    (set) => ({
      step: 'questionnaire',
      furthestStep: 'questionnaire',
      manifest: null,
      trustNames: [],
      uploadedDocIds: [],
      mandatoryUploads: {},
      clientName: '',
      trustHasCompany: {},
      skippedGaps: [],

      // Advancing ratchets the high-water mark forward; going back leaves it.
      setStep: (step) =>
        set((s) => ({
          step,
          furthestStep:
            stepIndex(step) > stepIndex(s.furthestStep) ? step : s.furthestStep,
        })),

      setManifest: (manifest) => set({ manifest }),
      setTrustNames: (trustNames) => set({ trustNames }),
      setClientName: (clientName) => set({ clientName }),
      setAccountKind: (accountKind) => set({ accountKind }),
      setTrustHasCompany: (trustIndex, hasCompany) =>
        set((s) => ({
          trustHasCompany: { ...s.trustHasCompany, [trustIndex]: hasCompany },
        })),

      addUploadedDocId: (id) =>
        set((s) =>
          s.uploadedDocIds.includes(id)
            ? s
            : { uploadedDocIds: [...s.uploadedDocIds, id] },
        ),

      setMandatoryUpload: (key, value) =>
        set((s) => ({ mandatoryUploads: { ...s.mandatoryUploads, [key]: value } })),
      clearMandatoryUpload: (key) =>
        set((s) => {
          if (!(key in s.mandatoryUploads)) return s;
          const next = { ...s.mandatoryUploads };
          delete next[key];
          return { mandatoryUploads: next };
        }),

      skipGap: (key) =>
        set((s) =>
          s.skippedGaps.includes(key)
            ? s
            : { skippedGaps: [...s.skippedGaps, key] },
        ),
      unskipGap: (key) =>
        set((s) => ({ skippedGaps: s.skippedGaps.filter((k) => k !== key) })),

      clear: () =>
        set({
          step: 'questionnaire',
          furthestStep: 'questionnaire',
          manifest: null,
          trustNames: [],
          uploadedDocIds: [],
          mandatoryUploads: {},
          clientName: '',
          accountKind: undefined,
          trustHasCompany: {},
          skippedGaps: [],
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
